import { useEffect, useMemo, useState } from "react";
import { Modal, Text, Paper, Flex, Button, List, ThemeIcon, Box, Badge, Select, TextInput } from "@mantine/core";
import { IconInfoCircle, IconCircleCheck, IconDownload } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { downloadSystemVerificationPdf } from "@/utils/systemVerificationPdf";
import {
  formatRegistrationIdTypeLabel,
  getEuVatLabelForHeadquartersCountry,
  getEuVatPlaceholderForHeadquartersCountry,
  getRegistrationNameByCountry,
  headquartersCountrySupportsEuVatOption,
  headquartersUsesIndirectCountryRegistrationWeb,
} from "@/components/shared/constants/general.const";
import { frenchSirenSiretDigitsOnly, isFrenchSirenOrSiretDigitLength } from "@/shared/utils/franceSirenInput";

interface ApplyVerificationModalProps {
  opened: boolean;
  onClose: () => void;
  onApply: (payload: { registrationIdType: string; registrationNumber: string; }) => void | Promise<void>;
  isLoading?: boolean;
  verificationData?: any;
  headquartersCountry?: string;
  initialRegistrationIdType?: string | null;
  initialRegistrationNumber?: string;
}

export default function ApplyVerificationModal({
  opened,
  onClose,
  onApply,
  isLoading = false,
  verificationData = null,
  headquartersCountry = "",
  initialRegistrationIdType = null,
  initialRegistrationNumber = "",
}: ApplyVerificationModalProps) {
  const [verificationFeeCredits, setVerificationFeeCredits] = useState(0);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [registrationIdType, setRegistrationIdType] = useState<string | null>(null);
  const [registrationNumber, setRegistrationNumber] = useState("");

  useEffect(() => {
    if (!opened) return;
    trpc.paymentConfig.get.query().then((config) => {
      setVerificationFeeCredits(config.verificationFeeCredits);
    });
  }, [opened]);

  const downloadVerificationResult = async () => {
    if (!verificationData) return;
    setPdfDownloading(true);
    try {
      await downloadSystemVerificationPdf(verificationData as Record<string, unknown>);
    } catch (err) {
      console.error("[ApplyVerificationModal] PDF download failed", err);
    } finally {
      setPdfDownloading(false);
    }
  };

  const normalizedHq = headquartersCountry.trim();
  const isIndia = normalizedHq === "India";
  const isTaiwan = normalizedHq === "Taiwan";
  const isUnitedKingdom = normalizedHq === "United Kingdom";
  const isNorway = normalizedHq === "Norway";
  const isFrance = normalizedHq === "France";
  const isNewZealand = normalizedHq === "New Zealand";
  const isChinaMainland = normalizedHq === "China (Mainland)";
  const isIndirectCountry = headquartersUsesIndirectCountryRegistrationWeb(normalizedHq);
  const showEuVatOption = headquartersCountrySupportsEuVatOption(normalizedHq);
  const countryOfficialRegistryLabel =
    (normalizedHq && getRegistrationNameByCountry(normalizedHq)) || "Official company registration ID";

  const registrationTypeOptions = useMemo(() => {
    if (!normalizedHq) return [];
    if (isIndia) {
      return [
        { value: "LEI", label: "LEI (Preferred)" },
        { value: "GST", label: "GST" },
        { value: "CIN", label: "CIN" },
        { value: "UDM", label: "UDM (Udyam)" },
      ];
    }
    if (isTaiwan) {
      return [
        { value: "LEI", label: "LEI (Preferred)" },
        { value: "UBN", label: countryOfficialRegistryLabel || "Unified Business Number (UBN)" },
      ];
    }
    if (isUnitedKingdom) {
      return [
        { value: "LEI", label: "LEI (Preferred)" },
        { value: "CRN", label: countryOfficialRegistryLabel || "Company Registration Number (CRN)" },
      ];
    }
    if (isNorway) {
      return [
        { value: "LEI", label: "LEI (Preferred)" },
        { value: "NOGN", label: countryOfficialRegistryLabel || "Organisation Number" },
      ];
    }
    if (isFrance) {
      return [
        { value: "LEI", label: "LEI (Preferred)" },
        { value: "EU_VAT", label: getEuVatLabelForHeadquartersCountry(normalizedHq) },
        { value: "SIREN", label: countryOfficialRegistryLabel || "SIREN / SIRET" },
      ];
    }
    if (showEuVatOption) {
      return [
        { value: "LEI", label: "LEI (Preferred)" },
        { value: "EU_VAT", label: getEuVatLabelForHeadquartersCountry(normalizedHq) },
      ];
    }
    return [
      { value: "LEI", label: "LEI (Preferred)" },
      {
        value: isNewZealand
          ? "NZBN"
          : isChinaMainland
            ? isIndirectCountry
              ? "COUNTRY_REGISTRATION"
              : "USCC"
            : isIndirectCountry
              ? "COUNTRY_REGISTRATION"
              : "LOCAL_REGISTRY",
        label: countryOfficialRegistryLabel,
      },
    ];
  }, [
    normalizedHq,
    isIndia,
    isTaiwan,
    isUnitedKingdom,
    isNorway,
    isFrance,
    showEuVatOption,
    isNewZealand,
    isChinaMainland,
    isIndirectCountry,
    countryOfficialRegistryLabel,
  ]);

  const selectedRegistrationIdType =
    registrationIdType && registrationTypeOptions.some((option) => option.value === registrationIdType)
      ? registrationIdType
      : null;

  useEffect(() => {
    if (!opened || verificationData) return;
    const initialType = initialRegistrationIdType ?? null;
    const validInitialType =
      initialType && registrationTypeOptions.some((option) => option.value === initialType)
        ? initialType
        : null;
    setRegistrationIdType(validInitialType);
    setRegistrationNumber(initialRegistrationNumber ?? "");
  }, [
    opened,
    verificationData,
    initialRegistrationIdType,
    initialRegistrationNumber,
    registrationTypeOptions,
  ]);

  const usesLeiField = selectedRegistrationIdType === "LEI";
  const usesUsccShapeField =
    selectedRegistrationIdType === "USCC" ||
    (isChinaMainland && selectedRegistrationIdType === "COUNTRY_REGISTRATION");
  const usesNognField = selectedRegistrationIdType === "NOGN";
  const usesSirenField = selectedRegistrationIdType === "SIREN";

  const registrationNumberError = (() => {
    const value = registrationNumber.trim();
    if (!selectedRegistrationIdType) return "Registration ID Type is required";
    if (!value.length) return "Registration number is required";
    if (usesLeiField && value.length !== 20) return "LEI must be exactly 20 characters";
    if (usesUsccShapeField && value.length !== 18) return "USCC must be exactly 18 characters";
    if (usesNognField && value.replace(/\D/g, "").length !== 9) {
      return "Organisation number must be exactly 9 digits";
    }
    if (usesSirenField && !isFrenchSirenOrSiretDigitLength(frenchSirenSiretDigitsOnly(value))) {
      return "Enter 9-digit SIREN or 14-digit SIRET";
    }
    return null;
  })();

  const registrationPlaceholder = (() => {
    if (!selectedRegistrationIdType) return "Enter registration number";
    if (selectedRegistrationIdType === "LEI") return "20-character LEI";
    if (selectedRegistrationIdType === "EU_VAT") return getEuVatPlaceholderForHeadquartersCountry(normalizedHq);
    if (selectedRegistrationIdType === "USCC") return "18-character Unified Social Credit Code";
    if (selectedRegistrationIdType === "SIREN") return "Enter SIREN (9 digits) or SIRET (14 digits)";
    if (selectedRegistrationIdType === "NOGN") return "Enter organisation number (9 digits)";
    const typeLabel = formatRegistrationIdTypeLabel(selectedRegistrationIdType, normalizedHq);
    return `Enter ${typeLabel}`;
  })();

  const handleProceed = async () => {
    if (registrationNumberError || !selectedRegistrationIdType) return;
    await onApply({
      registrationIdType: selectedRegistrationIdType,
      registrationNumber: registrationNumber.trim(),
    });
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Text fw={500} fz="lg" c="slate.8">
            {verificationData ? "System Verification Result" : "Apply for Verification"}
          </Text>
        }
        centered
        size={verificationData ? "xl" : "lg"}
        padding="lg"
      >
        {verificationData ? (
          <Box>
            <Paper p="md" radius="md" mb="lg" bg="green.0" style={{ border: "1px solid var(--mantine-color-green-2)" }}>
              <Flex gap="sm" align="center">
                <ThemeIcon size={24} radius="xl" color="green" variant="light">
                  <IconCircleCheck size={18} />
                </ThemeIcon>
                <Text c="green.7" fw={600} size="sm">
                  System verification completed successfully!
                </Text>
              </Flex>
            </Paper>

            <Paper
              p="lg"
              withBorder
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            >
              <Flex direction="column" gap="lg">
                {verificationData.companyName && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY NAME
                    </Text>
                    <Text size="sm" fw={500}>
                      {verificationData.companyName}
                    </Text>
                  </Flex>
                )}

                {verificationData.companyAddress && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY ADDRESS
                    </Text>
                    <Text size="sm">{verificationData.companyAddress}</Text>
                  </Flex>
                )}

                {verificationData.dateOfIncorporation && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      DATE OF REGISTRATION OF CERTIFICATE
                    </Text>
                    <Text size="sm">{verificationData.dateOfIncorporation}</Text>
                  </Flex>
                )}

                {verificationData.registrationNumber && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      REGISTRATION NUMBER
                    </Text>
                    <Text size="sm">{verificationData.registrationNumber}</Text>
                  </Flex>
                )}

                {verificationData.companyType && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY TYPE
                    </Text>
                    <Text size="sm">{verificationData.companyType}</Text>
                  </Flex>
                )}

                {verificationData.companyStatus && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      COMPANY STATUS
                    </Text>
                    <Badge color="green" variant="light" size="md">
                      {verificationData.companyStatus}
                    </Badge>
                  </Flex>
                )}

                {(verificationData._verificationSource ||
                  verificationData.verificationSource ||
                  verificationData.indirectVerificationSource) && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      VERIFICATION SOURCE
                    </Text>
                    <Text size="sm" fw={500}>
                      {verificationData._verificationSource ??
                        verificationData.verificationSource ??
                        verificationData.indirectVerificationSource}
                    </Text>
                  </Flex>
                )}

                {verificationData.natureOfBusiness && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      NATURE OF BUSINESS
                    </Text>
                    <Text size="sm">{verificationData.natureOfBusiness}</Text>
                  </Flex>
                )}

                {verificationData.authorizedCapital && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      AUTHORIZED CAPITAL
                    </Text>
                    <Text size="sm">{verificationData.authorizedCapital}</Text>
                  </Flex>
                )}

                {verificationData.paidUpCapital && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      PAID UP CAPITAL
                    </Text>
                    <Text size="sm">{verificationData.paidUpCapital}</Text>
                  </Flex>
                )}

                {verificationData.email && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      EMAIL
                    </Text>
                    <Text size="sm">{verificationData.email}</Text>
                  </Flex>
                )}

                {verificationData.phoneNumber && (
                  <Flex direction="column">
                    <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                      PHONE NUMBER
                    </Text>
                    <Text size="sm">{verificationData.phoneNumber}</Text>
                  </Flex>
                )}

                {/* Additional fields that might be present */}
                {Object.keys(verificationData)
                  .filter(
                    (key) =>
                      ![
                        "companyName",
                        "registrationNumber",
                        "companyType",
                        "companyStatus",
                        "indirectVerificationSource",
                        "verificationSource",
                        "_verificationSource",
                        "dateOfIncorporation",
                        "companyAddress",
                        "natureOfBusiness",
                        "authorizedCapital",
                        "paidUpCapital",
                        "email",
                        "phoneNumber",
                      ].includes(key) &&
                      verificationData[key] &&
                      typeof verificationData[key] !== "object"
                  )
                  .map((key) => (
                    <Flex direction="column" key={key}>
                      <Text size="xs" c="slate.6" tt="uppercase" mb={4} fw={500}>
                        {key.replace(/([A-Z])/g, " $1").toUpperCase()}
                      </Text>
                      <Text size="sm">{verificationData[key]}</Text>
                    </Flex>
                  ))}
              </Flex>
            </Paper>

            <Flex justify="flex-end" gap="sm" mt="lg" wrap="wrap">
              <Button
                variant="light"
                leftSection={<IconDownload size={18} />}
                onClick={() => void downloadVerificationResult()}
                loading={pdfDownloading}
                disabled={pdfDownloading}
              >
                Download PDF
              </Button>
              <Button onClick={onClose}>Close</Button>
            </Flex>
          </Box>
        ) : (
          <>
            <Text fz="sm" c="slate.5" mb="md">
              Your profile will be checked against official registries. When automated verification succeeds, a
              verification fee in credits applies immediately. If your registration requires admin registry review,
              your profile moves to pending review and the same fee is charged only after an administrator approves
              your company to verified status.
            </Text>

            <Paper p="md" radius="lg" mb="md" bg="gray.0" style={{ border: "1px solid var(--mantine-color-gray-3)" }}>
              <Flex justify="space-between" align="center">
                <Text fz="sm" c="slate.7">
                  Verification credits to be deducted:
                </Text>
                <Text fz="sm" fw={700} c="primary.7">
                  {verificationFeeCredits}
                </Text>
              </Flex>
            </Paper>

            <Paper p="md" radius="lg" mb="md" withBorder>
              <Flex direction="column" gap="sm">
                <Select
                  label="Registration ID Type"
                  placeholder={normalizedHq ? "Select type" : "Select country in profile first"}
                  data={registrationTypeOptions}
                  value={selectedRegistrationIdType}
                  onChange={(value) => {
                    setRegistrationIdType(value);
                    setRegistrationNumber("");
                  }}
                  disabled={!normalizedHq}
                  required
                />
                <TextInput
                  label="Registration Number"
                  placeholder={registrationPlaceholder}
                  value={registrationNumber}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    if (usesLeiField) {
                      setRegistrationNumber(v.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 20));
                    } else if (usesUsccShapeField) {
                      setRegistrationNumber(v.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 18));
                    } else if (usesNognField) {
                      setRegistrationNumber(v.replace(/\D/g, "").slice(0, 9));
                    } else if (usesSirenField) {
                      setRegistrationNumber(frenchSirenSiretDigitsOnly(v));
                    } else {
                      setRegistrationNumber(v);
                    }
                  }}
                  maxLength={usesLeiField ? 20 : usesUsccShapeField ? 18 : usesNognField ? 9 : usesSirenField ? 20 : undefined}
                  disabled={!selectedRegistrationIdType}
                  error={registrationNumber.length > 0 ? registrationNumberError : null}
                  required
                />
              </Flex>
            </Paper>

            <Paper
              p="md"
              radius="lg"
              mb="xl"
              bg="primary.0"
              style={{ border: "1px solid var(--mantine-color-primary-1)" }}
            >
              <Flex gap="sm" align="flex-start">
                <ThemeIcon size={20} radius="xl" color="primary" variant="transparent">
                  <IconInfoCircle size={18} />
                </ThemeIcon>
                <Flex direction="column">
                  <Text fz="sm" fw={600} c="primary.6" mb="xs">
                    Verification Requirements
                  </Text>
                  <List
                    size="sm"
                    spacing={4}
                    styles={{
                      item: {
                        color: "var(--mantine-color-primary-6)",
                        fontSize: "13px",
                      },
                    }}
                  >
                    <List.Item>
                      Once verified, certain profile details used for authentication cannot be edited.
                    </List.Item>
                    <List.Item>All documents are securely stored and reviewed by our compliance team.</List.Item>
                    <List.Item>Admin-assisted registry verification is usually completed within 24 hours.</List.Item>
                  </List>
                </Flex>
              </Flex>
            </Paper>

            <Flex justify="flex-end">
              <Button onClick={() => void handleProceed()} loading={isLoading} disabled={isLoading || !!registrationNumberError}>
                Proceed
              </Button>
            </Flex>
          </>
        )}
      </Modal>
    </>
  );
}
